import { Fragment } from "react";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  Users,
  ShieldAlert,
  Scale,
  TrendingDown,
  Database,
  FileCheck,
  ClipboardCheck,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Reveal from "@/components/services/Reveal";
import HubSpotInlineForm from "@/components/HubSpotInlineForm";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import type { ServiceData } from "@/data/servicesData";
import swHero from "@/assets/services/software-solutions-services/hero.jpg";

interface Props {
  parentService: ServiceData;
  onCtaClick: () => void;
}



const AITransformationContent = ({ parentService, onCtaClick }: Props) => {
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const servicesPath = localizeHref("/en/services", lang);
  const softwareSolutionsPath = localizeHref("/en/services/software-solutions-services", lang);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* 1. Hero */}
      <section className="relative text-primary-foreground pt-44 pb-16 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={swHero}
            alt="AI Transformation Program — Software Solutions & Services — QbD Group"
            className="w-full h-full object-cover"
            fetchPriority="high"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/75 to-primary/50" />
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{ background: `linear-gradient(90deg, ${parentService.color}, transparent)` }}
        />

        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 text-xs text-primary-foreground/40 tracking-wide uppercase">
              <li>
                <Link to={localizeHref("/", lang)} className="hover:text-primary-foreground/70 transition-colors">
                  {lang === "es" ? "Inicio" : lang === "zh-cn" ? "首页" : "Home"}
                </Link>
              </li>
              <li aria-hidden="true" className="text-primary-foreground/20">/</li>
              <li>
                <Link to={servicesPath} className="hover:text-primary-foreground/70 transition-colors">
                  {lang === "es" ? "Servicios" : lang === "zh-cn" ? "服务" : "Services"}
                </Link>
              </li>
              <li aria-hidden="true" className="text-primary-foreground/20">/</li>
              <li>
                <Link to={softwareSolutionsPath} className="hover:text-primary-foreground/70 transition-colors">
                  Software Solutions and Services
                </Link>
              </li>
              <li aria-hidden="true" className="text-primary-foreground/20">/</li>
              <li className="text-primary-foreground/60 truncate max-w-[200px]">AI Transformation Program</li>
            </ol>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                Pragmatic AI adoption for Pharma and MedTech
              </h1>
              <div className="key-takeaways max-w-2xl mb-8 space-y-3">
                <p className="text-base text-primary-foreground/55 leading-relaxed">
                  Most AI messaging promises everything and proves nothing. We start somewhere duller and more useful. Where you actually are on the road from standardised to AI-enabled, and what your realistic next step is. Then we build it, validated, and ready for inspection.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent"
                  onClick={() => scrollTo("where-are-you")}
                >
                  Where are you on this road? <ChevronDown size={16} className="ml-2" />
                </Button>
                <Button
                  size="lg"
                  className="rounded-full px-8 shadow-lg hover:opacity-90 bg-white text-primary hover:bg-white/90"
                  onClick={onCtaClick}
                >
                  Book a maturity assessment <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="ph-block aspect-[4/3] w-full overflow-hidden"
              data-placeholder="Hero image to be supplied by design"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm text-ink-muted">Hero image, to be supplied</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Credibility bar */}
      <section className="bg-surface-alt border-y border-border">
        <div className="container mx-auto px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0">
            {[
              { value: "600+", label: "life sciences experts" },
              { value: "4 stages", label: "we support every one, not only the last" },
              { value: "ISO/IEC 42001", label: "readiness built into the roadmap" },
              { value: "FDA", label: "network of former FDA auditors" },
            ].map((stat, i) => (
              <div key={i} className="relative flex flex-col items-start lg:items-center px-0 lg:px-6">
                <span className="text-lg font-bold text-brand-navy">{stat.value}</span>
                <span className="text-sm text-ink-muted text-left lg:text-center mt-1">{stat.label}</span>
                {i < 3 && <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-10 w-px bg-border" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. The problem */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-coral mb-4">
              Unapproved tool use
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Impressive pilots. No measurable change.
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              AI adoption in pharma happens bottom-up. Employees experiment on their own, tools multiply, and nobody owns the risk. You get distraction, inconsistent operations and exposure you cannot evidence to an inspector.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
            {[
              {
                icon: Users,
                title: "Uncontrolled adoption",
                body: "Employees experiment with AI individually. Adoption is random, operations drift out of alignment and the organisation cannot see what is actually in use.",
              },
              {
                icon: ShieldAlert,
                title: "Security exposure",
                body: "Company data moves through tools nobody approved. No data classification, no supplier qualification, no audit trail.",
              },
              {
                icon: Scale,
                title: "Compliance risk",
                body: "AI reaches GxP processes without a validation approach, change control or documented oversight. That is a finding waiting to happen.",
              },
              {
                icon: TrendingDown,
                title: "Value that never lands",
                body: "The pilots look good. Very few companies can then show reduced costs, increased revenue, faster cycle times or improved quality.",
              },
            ].map((card, i) => (
              <Reveal key={i} delay={i * 60} className="bg-white border border-border rounded-xl p-6 lg:p-8">
                <card.icon size={32} strokeWidth={1.5} aria-hidden="true" className="text-accent-coral mb-5" />
                <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-3">{card.title}</h3>
                <p className="text-sm sm:text-base text-ink-muted leading-relaxed">{card.body}</p>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="bg-brand-navy py-12">
          <div className="container mx-auto px-6 lg:px-10">
            <p className="text-center text-white text-xl sm:text-2xl font-semibold max-w-[800px] mx-auto leading-snug">
              AI capability moves faster than your governance. Every month without a framework adds systems you will have to remediate later.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Digital maturity model */}
      <section className="bg-surface-alt py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
              DIGITAL MATURITY
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              You cannot automate chaos. You cannot AI-enable silos.
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              Most AI programmes in pharma fail for one reason. They start above the maturity the organisation actually has. Before we discuss your AI roadmap, we establish where you are today.
            </p>
          </Reveal>

          <Reveal className="bg-white border-l-4 border-accent-teal rounded-xl p-6 lg:p-8 mb-12">
            <p className="text-base sm:text-lg text-brand-navy italic leading-relaxed">
              Digital maturity is how well an organisation uses digital technology to support processes, make decisions, ensure compliance and create competitive advantage.
            </p>
          </Reveal>

          <div className="mb-12">
            <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">
              The five levels, and where our four stages sit
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-semibold text-brand-navy">Level</th>
                    <th className="text-left py-3 px-4 font-semibold text-brand-navy">Plant maturity</th>
                    <th className="text-left py-3 px-4 font-semibold text-brand-navy">What it looks like</th>
                    <th className="text-left py-3 px-4 font-semibold text-brand-navy">Our stage</th>
                  </tr>
                </thead>
                <tbody className="text-ink-muted">
                  {[
                    { level: "5", maturity: "Adaptive plant", look: "Autonomous, self-optimising, plug and play", stage: "Accelerate with AI" },
                    { level: "4", maturity: "Predictive plant", look: "Integrated plant network with real-time predictive analysis", stage: "Automate into Accelerate" },
                    { level: "3", maturity: "Connected plant", look: "High-level automation, integration and system standardisation", stage: "Automate" },
                    { level: "2", maturity: "Digital silos", look: "Islands of automation, manual processes still connecting them", stage: "Digitalize" },
                    { level: "1", maturity: "Predigital plant", look: "Manual and paper-based. SOPs and batch records on paper", stage: "Standardize" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="py-3 px-4 font-medium text-brand-navy">{row.level}</td>
                      <td className="py-3 px-4">{row.maturity}</td>
                      <td className="py-3 px-4">{row.look}</td>
                      <td className="py-3 px-4 font-medium text-accent-teal">{row.stage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">
              What maturity means in a regulated environment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Database, title: "Data integrity", body: "Measured against ALCOA+ principles" },
                { icon: FileCheck, title: "GxP compliance", body: "Across GMP, GLP and GCP" },
                { icon: ClipboardCheck, title: "Audit readiness", body: "Inspection preparedness at any moment" },
              ].map((item, i) => (
                <Reveal key={i} delay={i * 60} className="bg-white rounded-xl p-6 border border-border">
                  <item.icon size={28} strokeWidth={1.5} className="text-accent-teal mb-4" aria-hidden="true" />
                  <h4 className="font-semibold text-brand-navy mb-2">{item.title}</h4>
                  <p className="text-sm text-ink-muted">{item.body}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">
              Where the industry actually sits
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { figure: "70%", caption: "of pharma sits between Standardize and early Digitalize" },
                { figure: "20%", caption: "have core systems but struggle to connect them" },
                { figure: "10%", caption: "are ready for AI initiatives today" },
                { figure: "3 to 5", caption: "years is the typical digital transformation for a mid-size company" },
              ].map((stat, i) => (
                <Reveal key={i} delay={i * 60} className="bg-white rounded-xl p-6 border border-border">
                  <span className="ph text-2xl font-bold text-brand-navy" data-placeholder="Source required before publication">{stat.figure}</span>
                  <p className="text-sm text-ink-muted mt-2">{stat.caption}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal>
            <p className="text-base sm:text-lg text-brand-navy font-medium max-w-[720px]">
              We support all four stages. Most consultancies only sell the last one.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 5. The four-stage path */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-sky mb-4">
              GxP process sequencing
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Standardize, Digitalize, Automate, Accelerate with AI
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              Four stages, in order. Skipping one does not save time in a regulated environment. It creates rework, and it creates findings. You do not have to run them sequentially across the whole company, but you do have to run them in order per process.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-12">
            {[
              {
                stage: "Standardize",
                role: "Foundation",
                goal: "Establish consistent, repeatable, compliant processes.",
                hereIf: [
                  "The same compliance gaps keep recurring",
                  "Shifts and departments reconcile data manually",
                  "Meetings stall on whether the data can be trusted",
                  "Audit observations repeat year after year",
                  "Knowledge sits with individuals rather than in the process",
                ],
                weDo: [
                  "Process standardisation. SOP development and workflow optimisation",
                  "Quality frameworks. GxP gap assessment and remediation",
                  "Computer system validation fundamentals",
                  "Data governance. ALCOA+ implementation and data integrity programmes",
                ],
                promise: "We get you audit-ready and prepared for digitalisation.",
              },
              {
                stage: "Digitalize",
                role: "Enablement",
                goal: "Digitally enable the processes you have standardised.",
                hereIf: [
                  "Paper batch records and Excel are still doing real work",
                  "You are selecting or replacing a core system",
                  "Electronic records exist but validation happens at the end, not by design",
                ],
                weDo: [
                  "System selection. Not the best technology, the right fit for a regulated environment",
                  "Validation excellence. CSV, URS, DQ, IQ, OQ, PQ, and 21 CFR Part 11 compliance",
                  "Regulatory alignment. Annex 11, data integrity and inspection readiness",
                  "Change management. Adoption support, training and SOP updates",
                ],
                promise: "We do not just implement systems. We validate them for compliance.",
              },
              {
                stage: "Automate",
                role: "Integration",
                goal: "Connect validated systems so processes run end to end without manual handoffs.",
                hereIf: [
                  "Core systems exist and are validated, but people still move data between them",
                  "Reporting is assembled by hand from several sources",
                  "The same information gets entered twice",
                  "Integrations exist but are point to point and undocumented",
                  "Instrument, plant and IT data live in separate worlds",
                ],
                weDo: [
                  "System and data integration across ERP, MES, QMS and LIMS",
                  "Workflow automation across departments",
                  "IT and OT convergence",
                  "Master data governance and structured data foundations",
                  "Validation of interfaces and automated workflows",
                  "Cloud and infrastructure qualification",
                ],
                promise: "We remove the manual steps between your systems, and we validate the automation.",
              },
              {
                stage: "Accelerate with AI",
                role: "Innovation",
                goal: "Use AI and machine learning for predictive, strategic decisions.",
                hereIf: [
                  "Systems are integrated and the data underneath them is governed",
                  "You are already asking about predictive quality and continued process verification",
                ],
                weDo: [
                  "Readiness assessment. Whether you are genuinely ready for AI",
                  "AI governance framework. Validation, algorithm transparency, GxP compliance",
                  "Use case prioritisation. High value and low risk first",
                  "Compliance safeguards, so AI does not become your next audit risk",
                  "The full AI Transformation Program, set out further down this page",
                ],
                promise: "We help you innovate without compromising compliance.",
                caveat: "This only works on clean, integrated, governed data.",
              },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 60} className="bg-surface-alt rounded-xl p-6 lg:p-8 border border-border">
                <div className="flex items-baseline gap-3 mb-3">
                  <h3 className="text-xl font-semibold text-brand-navy">{s.stage}</h3>
                  <span className="text-sm text-accent-sky font-medium">({s.role})</span>
                </div>
                <p className="text-sm text-ink-muted mb-6 leading-relaxed">{s.goal}</p>

                <div className="mb-6">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-navy mb-3">You are here if</h4>
                  <ul className="space-y-2">
                    {s.hereIf.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-ink-muted">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-sky shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-navy mb-3">What we do</h4>
                  <ul className="space-y-2">
                    {s.weDo.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-ink-muted">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-teal shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-sm font-medium text-brand-navy">{s.promise}</p>
                {s.caveat && (
                  <p className="text-xs text-ink-muted mt-3 italic">{s.caveat}</p>
                )}
              </Reveal>
            ))}
          </div>

          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">What changes as you move</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { before: "Paper batch records", after: "Electronic batch records" },
                { before: "Manual data entry", after: "Automated data capture" },
                { before: "Siloed spreadsheets", after: "Integrated QMS and LIMS" },
                { before: "Manual handoffs between teams", after: "End-to-end automated workflows" },
                { before: "Reactive reporting", after: "Real-time dashboards" },
                { before: "Validation as an afterthought", after: "Validation by design" },
                { before: "Steering the plant manually", after: "Digital twin" },
              ].map((row, i) => (
                <Reveal key={i} delay={i * 40} className="flex items-center justify-between bg-surface-alt rounded-lg p-4">
                  <span className="text-sm text-ink-muted">{row.before}</span>
                  <ArrowRight size={16} className="text-accent-sky mx-3 shrink-0" aria-hidden="true" />
                  <span className="text-sm font-medium text-brand-navy text-right">{row.after}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. Where are you on this road */}
      <section id="where-are-you" className="bg-brand-navy py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-lime mb-4">
              Readiness decision checks
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight mb-5">
              Where are you on this road?
            </h2>
            <p className="text-base sm:text-lg text-white/80 max-w-[720px] mb-12 leading-relaxed">
              Answer these in order. The first "no" is where you start. That is the honest answer, and it is usually not the one people expect.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
            {[
              { q: "Are your processes documented, consistent and enforced?", outcome: "If no: Start with Standardize." },
              { q: "Are your key processes digital, validated and compliant?", outcome: "If no: Focus on Digitalize." },
              { q: "Do your systems talk to each other without manual handoffs?", outcome: "If no: Focus on Automate. This is where most companies actually are." },
              { q: "Is your data integrated, structured and governed?", outcome: "If no: Finish Automate before you start anything with AI." },
              { q: "Does your organisation want to apply advanced analytics and AI?", outcome: "If yes, and you answered yes to the first four: You are ready to Accelerate with AI." },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 60} className="bg-white/[0.05] border border-white/[0.15] rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent-lime/20 text-accent-lime text-sm font-bold shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-white font-medium mb-2">{item.q}</p>
                    <p className="text-white/70 text-sm">{item.outcome}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mb-12">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-6">Read your own indicators</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse text-white">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-4 font-semibold">Indicator</th>
                    <th className="text-left py-3 px-4 font-semibold">Standardize</th>
                    <th className="text-left py-3 px-4 font-semibold">Digitalize</th>
                    <th className="text-left py-3 px-4 font-semibold">Automate</th>
                    <th className="text-left py-3 px-4 font-semibold">Accelerate</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {[
                    { indicator: "Process documentation", s: "Inconsistent or informal", d: "Documented but siloed", a: "Documented and connected", x: "Integrated and dynamic" },
                    { indicator: "Data capture", s: "Paper and Excel", d: "Digital but fragmented", a: "Automated per system", x: "Automated and integrated" },
                    { indicator: "System validation", s: "Ad hoc and reactive", d: "Validated but standalone", a: "Interfaces validated too", x: "Validated and interconnected" },
                    { indicator: "Compliance approach", s: "Reactive firefighting", d: "Procedural and preventive", a: "Monitored and controlled", x: "Predictive and strategic" },
                    { indicator: "Analytics capability", s: "Descriptive, what happened", d: "Diagnostic, why it happened", a: "Near real-time reporting", x: "Predictive, what will happen" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-white/10 last:border-0">
                      <td className="py-3 px-4 font-medium text-white">{row.indicator}</td>
                      <td className="py-3 px-4">{row.s}</td>
                      <td className="py-3 px-4">{row.d}</td>
                      <td className="py-3 px-4">{row.a}</td>
                      <td className="py-3 px-4">{row.x}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { heading: "Standardize or Digitalize", body: "Start with the foundation. Process standardisation, system selection, validation and data governance.", label: "See our digitalisation services" },
              { heading: "Automate", body: "Your systems are validated but not connected. Integration, workflow automation and IT and OT convergence, all validated.", label: "See our integration services" },
              { heading: "Accelerate with AI", body: "Your data is governed and your systems are connected. The AI Transformation Program takes it from there.", label: "Book an AI readiness call" },
            ].map((opt, i) => (
              <Reveal key={i} delay={i * 60} className="bg-white rounded-xl p-6">
                <h4 className="text-lg font-semibold text-brand-navy mb-3">{opt.heading}</h4>
                <p className="text-sm text-ink-muted mb-6 leading-relaxed">{opt.body}</p>
                <Button
                  size="sm"
                  className="rounded-full bg-brand-navy text-white hover:bg-brand-navy/90"
                  onClick={onCtaClick}
                >
                  {opt.label} <ArrowRight size={14} className="ml-2" />
                </Button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Common scenarios */}
      <section className="bg-surface-alt py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-coral mb-4">
              Migration and lab triggers
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Four situations that bring companies to us
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              Nobody wakes up wanting a maturity model. They wake up with a migration deadline, a lab that will not reconcile, or a board asking about AI. These are the four situations we see most often, and where each one sits on the road.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              {
                label: "DIGITALIZE AND AUTOMATE",
                title: "SAP upgrade or migration",
                trigger: "An S/4HANA migration, a major version upgrade, or a move to cloud ERP with GxP scope.",
                wrong: [
                  "Validation effort is underestimated and becomes the critical path",
                  "Data migration integrity is never properly evidenced",
                  "Interfaces to MES, QMS and LIMS break and are fixed informally",
                  "The implementation partner has no regulated-environment experience",
                ],
                do: [
                  "Validation strategy and execution alongside the migration, not after it",
                  "Data migration verification with inspection-ready evidence",
                  "Interface qualification across the connected systems",
                  "Change management, training and SOP updates",
                ],
                case: "Top-20 CDMO, S/4HANA migration across four GxP sites. Validation delivered inside the migration timeline, eleven weeks end to end, zero critical findings at the following inspection.",
                placeholder: "Real case required from pipeline, client approval pending",
              },
              {
                label: "ACCELERATE",
                title: "AI use cases in a regulated environment",
                trigger: "Leadership asks for AI, or pilots are already spreading through the business without governance.",
                wrong: [
                  "Pilots run on ungoverned data and cannot be defended to an inspector",
                  "No classification model, so every use case gets treated the same way",
                  "IT and OT data stay separate, so the interesting use cases are impossible",
                  "Nobody can show cost, cycle time or quality impact afterwards",
                ],
                do: [
                  "Readiness assessment against your actual maturity",
                  "AI governance framework, classification model and approval gates",
                  "IT and OT convergence and a structured data foundation",
                  "Use case prioritisation, high value and low risk first",
                  "The full AI Transformation Program set out below",
                ],
                case: "Global pharma group, fourteen manufacturing sites. AI governance framework, classification model and first use case portfolio live within five months of programme start.",
                placeholder: "Client name approval pending",
              },
              {
                label: "DIGITALIZE AND AUTOMATE",
                title: "Laboratory software and system integration",
                trigger: "A LIMS selection or replacement, instrument integration, or a lab still running spreadsheets alongside a validated LIMS.",
                wrong: [
                  "Instrument data stays outside the system and gets transcribed by hand",
                  "Data integrity findings land on lab records first",
                  "ELN, LIMS and ERP hold different versions of the same result",
                  "Integration is treated as an IT project with no quality involvement",
                ],
                do: [
                  "LIMS and ELN selection and implementation",
                  "Instrument interfacing and chromatography data system integration",
                  "Data integrity assessment and remediation",
                  "Validation of the systems and the interfaces between them",
                ],
                case: "Mid-size biotech, LIMS replacement with twelve instrument interfaces. Manual transcription removed from QC release, sample turnaround down by a third.",
                placeholder: "Real case required from pipeline",
              },
              {
                label: "STANDARDIZE THROUGH AUTOMATE",
                title: "IT infrastructure and cloud",
                trigger: "A cloud migration, a data centre exit, or infrastructure that has never been formally qualified.",
                wrong: [
                  "Qualification is treated as an IT housekeeping task",
                  "Supplier assessment of the cloud provider is missing or superficial",
                  "The shared responsibility model is never documented",
                  "No periodic review, so the qualified state quietly expires",
                ],
                do: [
                  "IT infrastructure qualification",
                  "Cloud supplier assessment and shared responsibility mapping",
                  "Periodic review and operational software compliance",
                  "Data integrity monitoring across the hosted environment",
                ],
                case: "European pharma manufacturer, data centre exit to cloud. Infrastructure qualified and shared responsibility documented ahead of the next regulatory inspection.",
                placeholder: "Real case required from pipeline",
                cardPlaceholder: "Scenario not confirmed, pending input",
              },
            ].map((s, i) => {
              const body = (
                <Reveal delay={i * 60} className={`bg-white rounded-xl p-6 lg:p-8 border border-border ${s.cardPlaceholder ? "ph-block" : ""}`} data-placeholder={s.cardPlaceholder}>
                  <span className="text-xs font-semibold tracking-widest uppercase text-accent-coral mb-2 block">{s.label}</span>
                  <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-3">{s.title}</h3>
                  <p className="text-sm text-ink-muted mb-6 leading-relaxed"><strong className="text-brand-navy">The trigger:</strong> {s.trigger}</p>

                  <div className="mb-6">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-navy mb-3">What usually goes wrong</h4>
                    <ul className="space-y-2">
                      {s.wrong.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-ink-muted">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-coral shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-navy mb-3">What we do</h4>
                    <ul className="space-y-2">
                      {s.do.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-ink-muted">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-teal shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="ph-block bg-surface-alt rounded-lg p-4" data-placeholder={s.placeholder}>
                    <p className="text-sm text-ink-muted italic">{s.case}</p>
                  </div>
                </Reveal>
              );
              return body;
            })}
          </div>

          <Reveal className="mt-12">
            <p className="text-base sm:text-lg text-brand-navy font-medium max-w-[720px]">
              Your situation may not be on this list. The approach does not change. We establish where you are, then we build the next step properly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 8. Our position */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
              Validation before scale
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Pragmatic AI adoption, not AI hype
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              You have heard that AI solves everything. It does not, and the firms saying so will not be there when an inspector asks how the model was validated. Our position is simpler. Embrace AI, in the best way possible for where you actually are.
            </p>
          </Reveal>

          <div className="mb-10">
            <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">What we will not do</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "We will not sell you an MES before your workflows are standardised",
                "We will not propose AI while your data integrity is in question",
                "We will not treat integration as something IT can finish alone",
                "We will not underestimate the validation effort",
                "We will not let anyone tell you that digital means compliant",
              ].map((item, i) => (
                <Reveal key={i} delay={i * 40} className="flex items-start gap-3 bg-surface-alt rounded-lg p-4">
                  <span className="mt-0.5 text-accent-coral" aria-hidden="true">
                    <Scale size={18} />
                  </span>
                  <p className="text-sm text-ink-muted">{item}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal>
            <div className="bg-brand-navy rounded-xl p-8">
              <p className="text-white text-lg sm:text-xl font-semibold leading-snug">
                A bad process digitised is just a faster bad process.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9. The program */}
      <section className="bg-surface-alt py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
              GxP workflow integration
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Pharma process understanding, AI expertise, and field experience
            </h2>
            <div className="max-w-[720px] mb-12 space-y-4">
              <p className="text-base sm:text-lg text-ink-muted leading-relaxed">
                Our AI Transformation Program comes from the QbD Software Solutions and Services Center of Excellence. The team has hands-on pharma industry experience and has run AI adoption inside regulated environments. The approach keeps evolving with what we learn in the field.
              </p>
              <p className="text-base sm:text-lg text-ink-muted leading-relaxed">
                This is not a parallel structure bolted onto your business. We adapt the programme to the systems and controls you already run.
              </p>
            </div>
          </Reveal>

          <div className="mb-8">
            <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">Built to integrate with what you already run</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Quality Management System",
                "IT and cyber security governance",
                "IT systems",
                "Data integrity and privacy programmes",
                "Computerised systems lifecycle controls (SDLC)",
                "Supplier and vendor management",
                "Training and competency",
                "Change control, non-conformance and CAPA",
                "Customer confidentiality obligations",
              ].map((item, i) => (
                <Reveal key={i} delay={i * 40} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-border">
                  <CheckCircle2 size={18} className="text-accent-teal shrink-0" aria-hidden="true" />
                  <span className="text-sm text-ink-muted">{item}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 10. Business outcomes */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-sky mb-4">
              BUSINESS CASE
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Define the vision first. The value follows.
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              We start from a blueprint built on industry experience and adapt it to your objectives. These are the outcomes our clients target.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
            {[
              {
                title: "Reduce effort",
                accent: "text-accent-teal",
                bullets: [
                  <>Cut administrative and documentation effort by up to <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">40 percent</span></>,
                  <>Reduce recurring deviations and manufacturing losses by <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">25 percent</span></>,
                  <>Improve asset reliability, unplanned downtime down <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">20 percent</span></>,
                ],
              },
              {
                title: "Move faster",
                accent: "text-accent-sky",
                bullets: [
                  <>Accelerate investigations and technical assessments from <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">11 days to 4</span></>,
                  <>Halve turnaround on responses to regulators and customers</>,
                  <>Cut evidence retrieval for regulatory activities from days to minutes</>,
                ],
              },
              {
                title: "See more",
                accent: "text-accent-lime",
                bullets: [
                  <>Improve access to internal knowledge, <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">80 percent</span> of queries answered without escalation</>,
                  <>Improve data and process understanding across <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">100 percent</span> of critical processes</>,
                  <>Improve capacity planning accuracy by <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">15 percent</span></>,
                ],
              },
              {
                title: "Build capability",
                accent: "text-accent-yellow",
                bullets: [
                  <>Improve training effectiveness, competency assessment pass rate up <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">30 percent</span></>,
                  <>Establish role-based AI competency across every site within <span className="ph" data-placeholder="Benchmark to be supplied by delivery team">12 months</span></>,
                ],
              },
            ].map((card, i) => (
              <Reveal key={i} delay={i * 60} className="bg-white rounded-xl p-6 lg:p-8 border border-border">
                <h3 className={`text-lg sm:text-xl font-semibold mb-4 ${card.accent}`}>{card.title}</h3>
                <ul className="space-y-3">
                  {card.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-ink-muted leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 shrink-0 rounded-full bg-current" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>

          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">Where AI earns its place</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Predictive quality, to prevent deviations before they happen",
                "Process optimisation, supporting continued process verification",
                "Anomaly detection through real-time monitoring",
                "Generative AI for documentation and trend analysis",
              ].map((item, i) => (
                <Reveal key={i} delay={i * 40} className="bg-surface-alt rounded-lg p-4">
                  <p className="text-sm text-ink-muted">{item}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 11. Governance */}
      <section className="bg-surface-alt py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
              Risk control framework
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Someone has to own AI. We build that structure.
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              Most AI risk in regulated companies comes from the absence of an owner. We put the structure in place first, then the controls that make it work day to day.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <Reveal className="bg-white rounded-xl p-6 lg:p-8 border border-border">
              <h3 className="text-lg font-semibold text-brand-navy mb-6">Structure we put in place</h3>
              <ol className="space-y-4">
                {[
                  "Executive sponsor and AI steering committee, with a defined approval mandate",
                  "An AI Center of Excellence that carries the operational tasks",
                  "Trained global and site AI champions",
                  "A quality oversight programme built into the model, not added later",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-ink-muted">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-accent-teal/10 text-accent-teal text-xs font-bold shrink-0">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ol>
            </Reveal>

            <Reveal delay={60} className="bg-white rounded-xl p-6 lg:p-8 border border-border">
              <h3 className="text-lg font-semibold text-brand-navy mb-6">Controls we install</h3>
              <ul className="space-y-3">
                {[
                  "Current-state assessment. Discovery of the AI already running across ERP, LIMS, MES and QMS",
                  "An AI use classification model, including uses your company prohibits",
                  "A formal use-case intake process with a defined approval gate",
                  "A controlled enterprise AI architecture that stops independent tool sprawl",
                  "Risk-based lifecycle assurance, so validation effort goes where the risk actually is",
                  "A role-based training programme",
                  "A programme metrics plan you can report to the board",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink-muted">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-teal shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 12. Roadmap */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-coral mb-4">
              GxP implementation sequence
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Six phases, from first control to continuous improvement
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              Once you reach the Accelerate stage, this is the implementation path. Phases overlap in practice. Scope and pace adapt to your site count and starting maturity.
            </p>
          </Reveal>

          <div className="space-y-6 mb-12">
            {[
              {
                phase: "Phase 1",
                name: "Mobilization",
                duration: "1 to 4 weeks",
                deliverables: [
                  "Executive sponsor appointed",
                  "AI steering committee formed",
                  "AI programme charter",
                  "Preliminary acceptable use rules",
                  "Current-state AI inventory",
                  "Initial communication strategy",
                  "Temporary controls for AI tools already in use",
                ],
              },
              {
                phase: "Phase 2",
                name: "Governance foundation",
                duration: "1 to 3 months",
                deliverables: [
                  "Enterprise AI policy",
                  "Risk classification model",
                  "Use-case intake form",
                  "Initial supplier requirements",
                  "Approved technology architecture",
                  "Data classification rules",
                  "All-employee awareness training",
                  "Initial use case portfolio",
                ],
              },
              {
                phase: "Phase 3",
                name: "Controlled pilots",
                duration: "3 to 6 months",
                deliverables: [
                  "Three to five pilots",
                  "Pilot protocols",
                  "Baseline metrics",
                  "User training",
                  "Risk assessments",
                  "Testing",
                  "Pilot reports",
                  "Go or no-go decisions",
                  "Lessons learned review",
                ],
              },
              {
                phase: "Phase 4",
                name: "Controlled scaling",
                duration: "6 to 12 months",
                deliverables: [
                  "AI Center of Excellence operational",
                  "Site champions in place",
                  "Full procedure set",
                  "Enterprise platform rollout",
                  "Approved knowledge repositories",
                  "GxP assurance framework",
                  "Supplier qualification programme",
                  "Monitoring dashboard",
                  "Internal audit programme",
                ],
              },
              {
                phase: "Phase 5",
                name: "Advanced integration",
                duration: "12 to 18 months",
                deliverables: [
                  "QMS, LIMS, MES and ERP integrations",
                  "Predictive maintenance",
                  "Advanced process analytics",
                  "Supply chain optimisation",
                  "Continued process verification support",
                  "Development and scale-up models",
                  "Controlled regulatory evidence applications",
                  "ISO/IEC 42001 readiness assessment",
                ],
              },
              {
                phase: "Phase 6",
                name: "Continuous improvement",
                duration: "18 months and beyond",
                deliverables: [
                  "Quarterly portfolio review",
                  "Annual AI management review",
                  "Periodic system reviews",
                  "Regulatory surveillance",
                  "Model performance monitoring",
                  "AI incident trending",
                  "Requalification following significant changes",
                  "Retirement of low value or unacceptable systems",
                ],
              },
            ].map((p, i) => (
              <Reveal key={i} delay={i * 60} className="bg-surface-alt rounded-xl p-6 lg:p-8 border border-border">
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 mb-4">
                  <span className="text-xs font-semibold tracking-widest uppercase text-accent-coral">{p.phase}</span>
                  <h3 className="text-lg font-semibold text-brand-navy">{p.name}</h3>
                  <span className="text-sm text-ink-muted">{p.duration}</span>
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {p.deliverables.map((d, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-ink-muted">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-coral shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="text-base sm:text-lg text-brand-navy font-medium max-w-[720px]">
              Phases 1 and 2 give you defensible control in under three months. Everything after that scales what already works.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 13. Why QbD Group */}
      <section className="bg-brand-navy py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-lime mb-4">
              Inspector-facing experience
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight mb-5">
              We support all four stages. Most firms sell one.
            </h2>
            <p className="text-base sm:text-lg text-white/80 max-w-[760px] mb-12 leading-relaxed">
              You will see the large consultancies in this process. We compete in the same rooms. We win because we can meet you at the stage you are actually at, and because our people have already had to defend this work in front of an inspector.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-8">
            {[
              {
                title: "Every stage, one partner",
                body: "Process standardisation, system implementation, integration and AI governance sit in one team. You do not change supplier every time you move a stage forward.",
              },
              {
                title: "We come out of the industry",
                body: "Pharma and MedTech operations experience, not generic transformation methodology ported into your context afterwards.",
              },
              {
                title: "Regulatory, digital and quality together",
                body: "System selection, validation excellence, regulatory alignment and change management in one place. Most firms hold one of the three and subcontract the rest.",
              },
              {
                title: "Tooling as well as advice",
                body: "Scilife, our quality management platform, brings AI into the regulated workflow itself. Most consultancies can only advise.",
              },
            ].map((point, i) => (
              <Reveal key={i} delay={i * 60} className="bg-white/[0.08] border border-white/[0.15] rounded-xl p-8 text-white">
                <h3 className="text-lg sm:text-xl font-semibold mb-3">{point.title}</h3>
                <p className="text-sm sm:text-base leading-relaxed text-white/90">{point.body}</p>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="text-white/80 text-base sm:text-lg max-w-[760px]">
              Inspection readiness support on this programme draws on our network of former FDA auditors.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 14. Proof */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
              Regulated delivery examples
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-12">
              What this looks like in practice
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {[
              {
                figure: "6 weeks",
                label: "US GO-LIVE",
                title: "Full eQMS live in six weeks",
                body: <>Implementation, configuration, training and validation delivered in six weeks for <span className="ph" data-placeholder="Client name approval pending">a US aesthetics manufacturer</span>, using standard configuration blueprints and a pre-built validation package.</>,
              },
              {
                figure: "14",
                label: "SITES",
                title: "Corporate AI transformation programme",
                body: <>A multi-site AI adoption programme for <span className="ph" data-placeholder="Client name approval pending">a global pharma group</span>, covering governance, use case portfolio and GxP assurance across fourteen manufacturing sites.</>,
              },
              {
                figure: "600+",
                label: "EXPERTS",
                title: "Delivery capacity behind the programme",
                body: "Validation, quality, regulatory and IT specialists across Europe and the United States, so the programme scales with your site count instead of stalling on availability.",
              },
            ].map((card, i) => (
              <Reveal key={i} delay={i * 60} className="bg-white rounded-xl p-8 border border-border">
                <div className="text-[44px] font-extrabold text-brand-navy leading-none mb-3">{card.figure}</div>
                <div className="text-xs font-semibold tracking-wider uppercase text-ink-muted mb-4">{card.label}</div>
                <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-3">{card.title}</h3>
                <p className="text-sm sm:text-base text-ink-muted leading-relaxed">{card.body}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="ph-block bg-surface-alt rounded-xl p-10" data-placeholder="Quote to be drafted and approved by the reference contact">
            <blockquote className="text-lg sm:text-xl text-brand-navy italic leading-relaxed mb-6">
              "We had run digital projects before. What we had not had was a partner who could tell us, honestly, that we were not ready for the thing we were asking for. That conversation saved us a year."
            </blockquote>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-border" aria-hidden="true" />
              <div>
                <p className="font-semibold text-brand-navy">Reference contact name</p>
                <p className="text-sm text-ink-muted">Chief Quality and Compliance Officer, Client company</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 15. FAQ */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Scope and validation</p>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">
            Before you start
          </h2>

          <Accordion type="single" collapsible className="w-full">
            {[
              {
                q: "Everyone says AI will fix everything. Why are you being cautious?",
                a: "Because in a regulated environment the claim has to survive an inspection. AI applied to ungoverned data creates risk, not advantage. We would rather tell you your next step is integration than sell you a programme you cannot defend.",
              },
              {
                q: "We want to skip to AI like our competitors. Why start with maturity?",
                a: "AI on poor data is a compliance risk. We audit your data readiness first. In most cases that gets you to a working AI programme faster, because you are not remediating while you scale.",
              },
              {
                q: "We already have a QMS and an MES. Are we not digital already?",
                a: "Possibly digital, probably not connected. The questions are whether those systems are validated, whether they talk to each other without manual handoffs, and whether you get predictive insight or just digital paper. Most companies we meet are at the Automate stage and think they are further.",
              },
              {
                q: "What exactly is the Automate stage?",
                a: "The gap between having validated systems and having them work together. Integration across ERP, MES, QMS and LIMS, workflow automation across departments, IT and OT convergence, and validation of the interfaces. It is where most of the industry actually sits, and it is where the fastest return usually is.",
              },
              {
                q: "Digital transformation takes too long.",
                a: "Not if it is phased. Standardize first and you see return inside six months, then digitalize. A phased approach reaches value faster than a single large programme.",
              },
              {
                q: "We are too small for this framework.",
                a: "Size is not the driver, complexity is. Small companies still need audit-ready processes. We scale the approach to your reality.",
              },
              {
                q: "Does the AI programme only apply to GxP systems?",
                a: "No. It covers your whole AI estate. The classification model then decides how much control each use case needs. A meeting summariser and a model that touches batch release do not get the same treatment.",
              },
              {
                q: "How does this relate to computer system validation?",
                a: "Validation is one control inside the programme, not the programme itself. We apply risk-based lifecycle assurance so validation effort goes where the risk sits.",
              },
              {
                q: "What is ISO/IEC 42001 and do we need it?",
                a: "ISO/IEC 42001 is the management system standard for artificial intelligence. Certification is optional today. Readiness is not, because it shapes what auditors and customers will ask you for. Phase 5 of our roadmap includes a readiness assessment.",
              },
              {
                q: "How quickly do we see something concrete?",
                a: "A maturity assessment takes days, not months. If you go into the AI programme, phase 1 runs in one to four weeks and ends with a charter, an inventory and temporary controls.",
              },
            ].map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-medium text-foreground hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 16. Related content */}
      <section className="bg-surface-alt py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-sky mb-4">
              CSV and GxP reading
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-12">
              Related content
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { type: "Webinar", title: "AI in Life Sciences" },
              { type: "Whitepaper", title: "The Essential CSV and Digitalisation FAQ" },
              { type: "Whitepaper", title: "Digitalisation in the Pharmaceutical Industry" },
              { type: "Webinar", title: "Fast GxP Compliance" },
              { type: "Case study", title: "Building a unified QMS for a non-profit lab organisation" },
              { type: "Whitepaper", title: "Annex 11 revision and digital compliance" },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 60} className="bg-white rounded-xl border border-border overflow-hidden">
                <div className="ph-block aspect-video bg-surface-alt" data-placeholder="Thumbnail and destination URL to be confirmed" />
                <div className="p-6">
                  <span className="text-xs font-semibold tracking-widest uppercase text-accent-sky mb-2 block">{item.type}</span>
                  <h3 className="text-base font-semibold text-brand-navy mb-4">{item.title}</h3>
                  <button className="text-sm font-medium text-accent-sky hover:underline">
                    Read more
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 17. Contact */}
      <section className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-coral mb-4">
              Maturity assessment discussion
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
              Get in touch
            </h2>
            <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
              Tell us where you are today. We will tell you honestly what your next stage should be, and whether you need us for it.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-10">
            <div className="rounded-xl border border-border p-6 lg:p-8 bg-white">
              <HubSpotInlineForm formId="3963ceac-3c61-4c9e-879c-a69911b81de5" />
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default AITransformationContent;
